/**
 * War Room Funnels View (MA-UI-023)
 * 
 * Global funnel analytics dashboard for the War Room.
 * Shows aggregated funnel performance across all funnels.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  CheckCircle2, 
  Calendar,
  Target,
  TrendingUp,
  TrendingDown,
  DollarSign,
  GitBranch,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import OutcomeDistribution from './OutcomeDistribution';
import ExecutiveAlerts from './ExecutiveAlerts';
import FunnelHealthWidget from './FunnelHealthWidget';
import PredictionCard from './PredictionCard';

function useOutcomeDistributionData(period, funnelId, campaignId, tenantId) {
  return useQuery({
    queryKey: ['outcome-distribution', period, funnelId, campaignId, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (funnelId) params.append('funnel_id', funnelId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (tenantId) params.append('tenant_id', tenantId);
      const res = await api.get(`/funnel-analytics/outcome-distribution?${params}`);
      return res.data;
    },
    staleTime: 60000,
  });
}

// =============================================================================
// METRIC CARD COMPONENT
// =============================================================================

function MetricCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'indigo', emptyState }) {
  const colorClasses = {
    indigo: 'text-indigo-400 bg-indigo-500/10',
    green: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    red: 'text-red-400 bg-red-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
  };

  const isEmptyOrZero = value === null || value === undefined || value === 0 || value === '0' || value === '0%' || value === '$0';

  return (
    <Card className="glass border-white/10">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-200/60 mb-1">{title}</p>
            {isEmptyOrZero && emptyState ? (
              <p className="text-lg text-slate-200/40 italic">{emptyState}</p>
            ) : (
              <p className="text-2xl font-bold text-slate-100">{value}</p>
            )}
            {subtitle && (
              <p className="text-xs text-slate-200/60 mt-1">{subtitle}</p>
            )}
            {trend !== undefined && trend !== null && (
              <div className={`flex items-center gap-1 mt-1.5 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                <span>{trend >= 0 ? '+' : ''}{trendValue}%</span>
              </div>
            )}
          </div>
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// API HOOKS
// =============================================================================

function useFunnelOverview(period, tenantId, campaignId) {
  return useQuery({
    queryKey: ['funnel-analytics-overview', period, tenantId, campaignId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (tenantId) params.append('tenant_id', tenantId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      
      const res = await api.get(`/funnel-analytics/overview?${params}`);
      return res.data;
    },
    staleTime: 60000,
  });
}

function useFunnelSummaries(period, tenantId, campaignId) {
  return useQuery({
    queryKey: ['funnel-analytics-summaries', period, tenantId, campaignId],
    queryFn: async () => {
      const params = new URLSearchParams({ period, limit: '20' });
      if (tenantId) params.append('tenant_id', tenantId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      
      const res = await api.get(`/funnel-analytics/funnels?${params}`);
      return res.data;
    },
    staleTime: 60000,
  });
}

// =============================================================================
// FUNNEL TABLE ROW
// =============================================================================

function FunnelTableRow({ funnel, onSelect }) {
  // Calculate estimated revenue (placeholder)
  const estimatedRevenue = null; // Would come from funnel settings
  
  return (
    <tr 
      className="hover:bg-white/5 cursor-pointer transition-colors"
      onClick={() => onSelect(funnel.funnel_id)}
      data-testid={`funnel-row-${funnel.funnel_id}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-slate-100 font-medium hover:text-indigo-400 transition-colors">
            {funnel.funnel_name}
          </span>
          <ChevronRight className="w-4 h-4 text-slate-200/40" />
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-slate-200/80">{funnel.sessions}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <Badge 
          variant="outline" 
          className={funnel.completion_rate >= 20 
            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
            : 'bg-amber-500/10 text-amber-400 border-amber-500/30'
          }
        >
          {funnel.completion_rate}%
        </Badge>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-blue-400">{funnel.booking_rate}%</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={
          funnel.qualification_rate >= 30 
            ? 'text-green-400 font-medium' 
            : funnel.qualification_rate >= 15 
            ? 'text-amber-400' 
            : 'text-slate-200/60'
        }>
          {funnel.qualification_rate || 0}%
        </span>
        <span className="text-slate-200/40 text-xs ml-1">({funnel.qualified_count || 0})</span>
      </td>
      <td className="px-4 py-3 text-center">
        {estimatedRevenue !== null ? (
          <span className="text-purple-400">${estimatedRevenue.toLocaleString()}</span>
        ) : (
          <span className="text-slate-200/40 text-sm italic">-</span>
        )}
      </td>
    </tr>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function WarRoomFunnelsView({ period, campaignId, tenantId }) {
  const navigate = useNavigate();
  const [selectedFunnelFilter, setSelectedFunnelFilter] = useState('all');
  
  const { data: overview, isLoading: overviewLoading } = useFunnelOverview(period, tenantId, campaignId);
  const { data: funnels, isLoading: funnelsLoading } = useFunnelSummaries(period, tenantId, campaignId);
  const { data: outcomeData } = useOutcomeDistributionData(
    period,
    selectedFunnelFilter !== 'all' ? selectedFunnelFilter : undefined,
    campaignId,
    tenantId,
  );
  
  // Calculate aggregated metrics
  const totalSessions = overview?.total_sessions || 0;
  const completionRate = overview?.completion_rate || 0;
  const bookingRate = overview?.booking_rate || 0;
  const qualifiedCount = overview?.qualified_count || 0;
  const qualificationRate = totalSessions > 0 ? ((qualifiedCount / totalSessions) * 100).toFixed(1) : 0;
  
  // Estimated revenue (placeholder until revenue assumptions are implemented)
  const estimatedRevenueTotal = null; // Would be aggregated from all funnels
  
  // Filter funnels
  const filteredFunnels = selectedFunnelFilter === 'all' 
    ? funnels 
    : funnels?.filter(f => f.funnel_id === selectedFunnelFilter);
  
  // Handle funnel click - navigate to funnel details analytics tab
  const handleFunnelSelect = (funnelId) => {
    navigate(`/app/funnels/${funnelId}?tab=analytics`);
  };
  
  const hasData = totalSessions > 0;

  return (
    <div className="space-y-6" data-testid="warroom-funnels-view">
      {/* Filter Row */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-100">Funnel Performance</h3>
          <p className="text-sm text-slate-200/60">
            {hasData 
              ? `${totalSessions.toLocaleString()} total sessions across ${funnels?.length || 0} funnels`
              : 'No funnel sessions in this period'
            }
          </p>
        </div>
        
        {/* Funnel Filter Dropdown */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-200/60" />
          <Select value={selectedFunnelFilter} onValueChange={setSelectedFunnelFilter}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10 text-slate-100">
              <SelectValue placeholder="All Funnels" />
            </SelectTrigger>
            <SelectContent className="glass border-white/10">
              <SelectItem value="all">All Funnels</SelectItem>
              {funnels?.map((funnel) => (
                <SelectItem key={funnel.funnel_id} value={funnel.funnel_id}>
                  {funnel.funnel_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Total Sessions"
          value={overviewLoading ? '...' : totalSessions.toLocaleString()}
          icon={Users}
          trend={overview?.sessions_change_pct}
          trendValue={overview?.sessions_change_pct?.toFixed(1)}
          color="indigo"
          emptyState="No sessions"
        />
        <MetricCard
          title="Avg Completion Rate"
          value={overviewLoading ? '...' : `${completionRate}%`}
          subtitle={`${overview?.completed_sessions || 0} completed`}
          icon={CheckCircle2}
          color="green"
          emptyState="No data"
        />
        <MetricCard
          title="Avg Booking Rate"
          value={overviewLoading ? '...' : `${bookingRate}%`}
          icon={Calendar}
          color="blue"
          emptyState="No bookings"
        />
        <MetricCard
          title="Avg Qualification Rate"
          value={overviewLoading ? '...' : `${qualificationRate}%`}
          subtitle={`${qualifiedCount} qualified`}
          icon={Target}
          color="amber"
          emptyState="No data"
        />
        <MetricCard
          title="Est. Revenue (All)"
          value={estimatedRevenueTotal !== null ? `$${estimatedRevenueTotal.toLocaleString()}` : null}
          icon={DollarSign}
          color="purple"
          emptyState="Revenue config required"
        />
      </div>

      {/* MA-ALERTS-ENGINE-008: Executive Alerts Panel (with prediction context) */}
      <ExecutiveAlerts
        alerts={outcomeData?.alerts || []}
        prediction={outcomeData?.prediction}
        onAlertClick={(alert) => {
          if (alert.funnel_id) {
            handleFunnelSelect(alert.funnel_id);
          }
        }}
      />

      {/* MA-PREDICTION-ENGINE-010: Prediction Widget */}
      <PredictionCard prediction={outcomeData?.prediction} />

      {/* MA-ANALYTICS-OUTCOME-DASH-005: Outcome Distribution + Qualification Rate + Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <OutcomeDistribution
          period={period}
          funnelId={selectedFunnelFilter !== 'all' ? selectedFunnelFilter : undefined}
          campaignId={campaignId}
          tenantId={tenantId}
        />
        
        {/* Qualification Rate Highlight */}
        <Card className="glass border-white/10" data-testid="qualification-rate-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-100">
              <Target className="w-5 h-5 text-green-400" />
              Qualification Rate
            </CardTitle>
            <CardDescription className="text-slate-200/60">
              Percentage of leads that completed funnels as qualified
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-4">
              <div className="text-center">
                <p className="text-5xl font-bold text-green-400" data-testid="global-qualification-rate">
                  {overviewLoading ? '...' : `${qualificationRate}%`}
                </p>
                {outcomeData && (outcomeData.prev_total_leads > 0 || outcomeData.qualification_rate_delta !== 0) && (
                  <p className={`text-sm font-medium mt-1 ${
                    outcomeData.qualification_rate_delta > 0 ? 'text-green-400' :
                    outcomeData.qualification_rate_delta < 0 ? 'text-red-400' :
                    'text-slate-400'
                  }`} data-testid="qualification-rate-trend">
                    {outcomeData.qualification_rate_delta > 0 ? '↑' : outcomeData.qualification_rate_delta < 0 ? '↓' : '→'}
                    {' '}{outcomeData.qualification_rate_delta > 0 ? '+' : ''}{outcomeData.qualification_rate_delta}% vs prev period
                  </p>
                )}
                <p className="text-sm text-slate-200/50 mt-2">
                  {qualifiedCount} qualified of {totalSessions} sessions
                </p>
              </div>
            </div>
            {/* Per-funnel qualification rates */}
            {funnels?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                <p className="text-xs text-slate-200/50 uppercase tracking-wide mb-2">By Funnel</p>
                {funnels.slice(0, 5).map((f) => (
                  <div 
                    key={f.funnel_id} 
                    className="flex items-center justify-between text-sm cursor-pointer hover:bg-white/5 px-2 py-1 rounded"
                    onClick={() => handleFunnelSelect(f.funnel_id)}
                  >
                    <span className="text-slate-200/70 truncate max-w-[60%]">{f.funnel_name}</span>
                    <span className={
                      (f.qualification_rate || 0) >= 30 ? 'text-green-400 font-medium' :
                      (f.qualification_rate || 0) >= 15 ? 'text-amber-400' :
                      'text-slate-200/50'
                    }>
                      {f.qualification_rate || 0}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* MA-FUNNEL-HEALTH-SCORE-013: Funnel Health Widget */}
        <FunnelHealthWidget tenantId={tenantId} />
      </div>

      {/* Funnel Performance Table */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <GitBranch className="w-5 h-5 text-indigo-400" />
            Funnel Performance Table
          </CardTitle>
          <CardDescription className="text-slate-200/60">
            Click on a funnel name to view detailed analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {funnelsLoading ? (
            <div className="text-center py-8 text-slate-200/60">Loading funnels...</div>
          ) : filteredFunnels?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-200/60">Funnel Name</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-slate-200/60">Sessions</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-slate-200/60">Completion Rate</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-slate-200/60">Booking Rate</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-slate-200/60">Qualification Rate</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-slate-200/60">Est. Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredFunnels.map((funnel) => (
                    <FunnelTableRow 
                      key={funnel.funnel_id} 
                      funnel={funnel} 
                      onSelect={handleFunnelSelect}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <GitBranch className="w-12 h-12 mx-auto text-slate-200/40 mb-4" />
              <h3 className="text-lg font-medium text-slate-200/60 mb-2">No funnel session data yet</h3>
              <p className="text-sm text-slate-200/40">
                Funnel analytics will appear here once visitors start interacting with your funnels.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
